import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const ServiceWorkerContext = createContext({
  updateAvailable: false,
  registration: null,
  applyUpdate: () => {},
});

export const useServiceWorker = () => useContext(ServiceWorkerContext);

export const ServiceWorkerProvider = ({ children }) => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [registration, setRegistration] = useState(null);

  // Debug helper: window.__testSwUpdate() to simulate update
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      window.__testSwUpdate = () => setUpdateAvailable(true);
      window.__clearSwUpdate = () => setUpdateAvailable(false);
    }
  }, []);

  const onUpdate = useCallback((reg) => {
    setUpdateAvailable(true);
    setRegistration(reg);
  }, []);

  const applyUpdate = useCallback(() => {
    if (registration && registration.waiting) {
      // Listen for the new service worker to take control before reloading
      // This prevents a white screen from loading before the new SW is ready
      const onControllerChange = () => {
        console.log('[ServiceWorker] Controller changed, reloading...');
        window.location.reload();
      };

      navigator.serviceWorker.addEventListener('controllerchange', onControllerChange, { once: true });

      // Tell the waiting service worker to skip waiting
      console.log('[ServiceWorker] Sending SKIP_WAITING...');
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });

      // Fallback: If controllerchange doesn't fire within 3 seconds, reload anyway
      setTimeout(() => {
        console.log('[ServiceWorker] Fallback reload (controllerchange timeout)');
        navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
        window.location.reload();
      }, 3000);
    } else {
      // No waiting worker (dev mode test), just reload
      window.location.reload();
    }
  }, [registration]);

  return (
    <ServiceWorkerContext.Provider value={{ updateAvailable, registration, applyUpdate, onUpdate }}>
      {children}
    </ServiceWorkerContext.Provider>
  );
};

export default ServiceWorkerContext;
